import React, { useCallback, useRef, useState, useEffect } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { groupBy, isEmpty, filter, without, omit, uniqBy } from 'lodash-es';

import InfiniteScroll from 'react-infinite-scroll-component';

import {
  MenuItem,
  ShowOnlySelected,
  MenuHeader,
  MenuDivider,
  MenuGroup,
  SubMenu,
  CustomLabelCheckbox,
  useMenuProps,
  useShadowLine,
} from '../../../../DropdownSet';
import SearchField from '../../../../Search/SearchField';
import LoadingSpinner from '../../../../LoadingSpinner';
import AppIcon from '../../../../AppIcon';

import api from '../../../api';
import { unifiedRefNoBy } from '../../../../../utils/helpers';
import { CROSS_APP_PANEL_VERSION_MAPPING } from '../../../../../utils/advanced-metrics';

const infiniteScrollStyles = { overflow: 'visible' };

const renderOptionLabel = (app, { size = 24, className } = {}) => (
  <div className={cx('paired-apps-dropdown-label d-flex align-items-center', className)}>
    <AppIcon
      alt={app.name}
      url={app.icon_url}
      store={app.store}
      size={size}
      hideHtmlTitle
      withBorder
      containerClasses="paired-apps-dropdown-label__icon"
    />
    <span className="paired-apps-dropdown-label__name text-truncate">{app.name}</span>
  </div>
);

const CrossAppPairingsSubMenu = ({
  selected,
  app = {},
  groupAppsBy = 'sector',
  panelVersion,
  currentRefNo,
  crossAppsPairsDecorated,
  onChange,
  onCurrentRefNoChange,
}) => {
  const subMenuInstanceRef = useRef();
  const subMenuProps = useMenuProps({
    menuStyle: { width: 401 },
    className: 'paired-apps-dropdown-submenu',
  });
  const [shadowLine, onScroll] = useShadowLine();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSelected, setShowSelected] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [localTotal, setTotal] = useState(0);

  const fetchCrossAppsDataRequest = (ref_no, offset, query) => {
    setError(false);
    setLoading(true);
    api.fetchCrossAppsData({ params: {
      unified_ref_no: ref_no,
      offset: offset || 0,
      limit: 20,
      query,
      panel_version: CROSS_APP_PANEL_VERSION_MAPPING[panelVersion],
    } })
      .then(({ data: { related_apps_list, total } }) => {
        setLoading(false);
        setTotal(total);
        const unifiedRefNosList = related_apps_list.map(related_app =>
          ({ ...related_app, ref_no: unifiedRefNoBy({ ...related_app, kind: 'app' }) }));
        setSearchOptions(prev => uniqBy([...prev, ...unifiedRefNosList], 'ref_no'));
      }).catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  const searchInstanceRef = useRef();

  const handlePairsSearch = (value) => {
    if (value.ref_no === currentRefNo) return;
    onCurrentRefNoChange(value.ref_no);
    setShowSelected(false);
    setSearchQuery('');
    searchInstanceRef.current.clearSearch(true);
    setSearchOptions([]);
    setTotal(0);
    fetchCrossAppsDataRequest(value.ref_no);
  };

  const handleSearchChange = (value) => {
    setShowSelected(false);
    setSearchOptions([]);
    setTotal(0);
    fetchCrossAppsDataRequest(currentRefNo, 0, value);
  };

  useEffect(() => {
    setShowSelected(false);
    setSearchQuery('');
    searchInstanceRef.current?.clearSearch(true);
    setSearchOptions([]);
    setTotal(0);
  }, [panelVersion]);

  const handleChange = (e, value) => {
    e.keepOpen = true;
    const presentAppPair = selected[currentRefNo];
    if (!presentAppPair) {
      return onChange({ ...selected, [currentRefNo]: [value] });
    }

    const subList = selected[currentRefNo].includes(value)
      ? without(selected[currentRefNo], value)
      : [...selected[currentRefNo], value];

    const mainList = !isEmpty(subList)
      ? { ...selected, [currentRefNo]: subList }
      : omit(selected, [currentRefNo]);

    onChange(mainList);
  };

  const mainFiltered = filter(searchOptions,
    option => selected[currentRefNo]?.includes(option.ref_no));

  let nextSelectedOptions;
  if (showSelected && !isEmpty(selected[currentRefNo])) {
    if (mainFiltered.length > 0) {
      nextSelectedOptions = mainFiltered;
    } else {
      nextSelectedOptions = crossAppsPairsDecorated[currentRefNo].filter(
        option => selected[currentRefNo].includes(option.unified_app_id),
      );
    }
  } else {
    nextSelectedOptions = searchOptions;
  }

  const renderMenuOptions = (renderOptions) => {
    const renderMenuOption = (option, index) => {
      const { name, ref_no, disabled: isDisabled = false, unified_app_id } = option;
      const isChecked = unified_app_id
        ? selected[currentRefNo]?.includes(unified_app_id)
        : selected[currentRefNo]?.includes(ref_no);

      return (
        <MenuItem
          key={ref_no || index}
          value={ref_no}
          disabled={isDisabled}
          onClick={e => handleChange(e, unified_app_id || ref_no)}
        >
          <CustomLabelCheckbox
            id={name}
            disabled={isDisabled}
            checked={isChecked}
            labelText={renderOptionLabel(option, { className: 'paired-apps-dropdown-label--menu' })}
          />
        </MenuItem>
      );
    };

    if (groupAppsBy) {
      const groupedOptions = groupBy(renderOptions, groupAppsBy);
      const groupLabels = Object.keys(groupedOptions);

      return groupLabels.reduce((acc, groupLabel, index) => {
        const isLast = index === groupLabels.length - 1;

        return acc.concat(
          <MenuHeader key={groupLabel}>
            {groupLabel}
          </MenuHeader>,
          groupedOptions[groupLabel].map(renderMenuOption),
          !isLast ? <MenuDivider key={`${groupLabel}-divider`} fullWidth dense /> : [],
        );
      }, []);
    }

    return renderOptions.map(renderMenuOption);
  };

  const handleLoadMore = (ref_no, offset) => {
    fetchCrossAppsDataRequest(ref_no, offset);
  };

  const toggleShowSelected = useCallback((e) => {
    e.keepOpen = true;
    setShowSelected(e.checked);
  }, []);

  const isSearchActive = searchQuery.trim().length > 0;

  const fullRefNo = unifiedRefNoBy({ ...app, kind: 'app' });
  const scrollId = `paired-apps-menu-group-${fullRefNo}`;

  const noResults = !searchOptions.length;

  return (
    <SubMenu
      key={app.value}
      ref={subMenuInstanceRef}
      label={renderOptionLabel(app)}
      {...subMenuProps}
      onMenuChange={() => handlePairsSearch({ ...app, kind: 'app', ref_no: fullRefNo })}
    >
      <>
        <SearchField
          instanceRef={searchInstanceRef}
          onChange={handleSearchChange}
        />
        <MenuDivider key="divider" fullWidth dense />
        {!isSearchActive && (
          <ShowOnlySelected
            checked={showSelected}
            disabled={isEmpty(selected[fullRefNo])}
            onClick={toggleShowSelected}
          />
        )}
        <MenuDivider key="divider-2" fullWidth dense className="m-b-0" />
        {shadowLine}
        {!nextSelectedOptions?.length && loading && (
          <div className="paired-apps-dropdown-initial">
            <LoadingSpinner
              size="lg"
              id="paired-apps-dropdown-initial"
              className="text-blue justify-content-center"
            />
          </div>
        )}
        {!noResults && (
          <MenuGroup
            takeOverflow
            id={scrollId}
            className="paired-apps-dropdown__menu-group"
            style={{ height: 600 }}
            onScroll={onScroll}
          >
            <InfiniteScroll
              dataLength={nextSelectedOptions?.length || 0}
              hasMore={nextSelectedOptions?.length <= localTotal}
              scrollThreshold={0.95}
              scrollableTarget={scrollId}
              style={infiniteScrollStyles}
              className="paired-apps-dropdown__list"
              next={() => handleLoadMore(fullRefNo, nextSelectedOptions.length)}
            >
              {renderMenuOptions(nextSelectedOptions)}
              {loading && (
                <LoadingSpinner
                  size="sm"
                  id="paired-apps-dropdown-more"
                  className="text-blue justify-content-center"
                />
              )}
            </InfiniteScroll>
          </MenuGroup>
        )}
        {error && (
          <div className="text-danger text-center p-3">Error occurred while loading apps</div>
        )}
        {!loading && noResults && (
          <div className="text-center p-3">No results found</div>
        )}
      </>
    </SubMenu>
  );
};

CrossAppPairingsSubMenu.propTypes = {
  selected: T.object,
  app: T.object,
  options: T.array,
  disabled: T.bool,
  panelVersion: T.string,
  groupAppsBy: T.string,
  currentRefNo: T.string,
  unselectedStateText: T.string,
  onChange: T.func,
  onCurrentRefNoChange: T.func,
};

export default CrossAppPairingsSubMenu;
